// Kanzlei-Detail (Drill-in) — Kennzahlen + Sachbearbeiter + Provisioning-Aktionen.

import { useCallback, useEffect, useState, type FormEvent } from "react";

import { Button } from "@/components/Button";
import { Loader } from "@/components/Loader";

import {
  createBotKey,
  createFirmUser,
  getFirm,
  OperatorApiError,
  type FirmDetail,
} from "./api";
import { MandantenList } from "./MandantenList";
import { SecretBox } from "./SecretBox";
import { WorkflowsSection } from "./WorkflowsSection";

function Stat({
  label,
  value,
  onClick,
}: {
  label: string;
  value: number;
  onClick?: () => void;
}) {
  return (
    <div
      className={`op-card${onClick ? " op-card-clickable" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="op-card-value">{value}</div>
      <div className="op-card-label">
        {label}
        {onClick && <span className="op-card-more"> ›</span>}
      </div>
    </div>
  );
}

function AddUserForm({
  firmId,
  onDone,
  onCancel,
}: {
  firmId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !fullName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      // Passwort weglassen → das Backend generiert eins und gibt es einmalig zurück.
      const r = await createFirmUser(firmId, {
        email: email.trim(),
        full_name: fullName.trim(),
      });
      setPassword(r.generated_password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Anlegen");
    } finally {
      setBusy(false);
    }
  }

  if (password) {
    return (
      <div className="op-form">
        <p className="op-ok">Sachbearbeiter „{fullName}" angelegt.</p>
        <SecretBox
          title={`Passwort für ${email}`}
          secret={password}
          onDismiss={onDone}
        />
      </div>
    );
  }

  return (
    <form className="op-form" onSubmit={submit}>
      <h3 className="op-h3">Sachbearbeiter anlegen</h3>
      <label className="op-label">
        Name
        <input
          className="op-input"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          autoFocus
          required
        />
      </label>
      <label className="op-label">
        E-Mail
        <input
          className="op-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </label>
      {error && <p className="op-error">{error}</p>}
      <div className="op-form-actions">
        <Button
          variant="primary"
          type="submit"
          loading={busy}
          disabled={!email.trim() || !fullName.trim()}
        >
          Anlegen
        </Button>
        <button type="button" className="op-btn-ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function AddBotKeyForm({
  firmId,
  onDone,
  onCancel,
}: {
  firmId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState("Bot-Host");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [key, setKey] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await createBotKey(firmId, label.trim());
      setKey(r.bot_key);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Erzeugen");
    } finally {
      setBusy(false);
    }
  }

  if (key) {
    return (
      <div className="op-form">
        <SecretBox title="Bot-Host-Key" secret={key} onDismiss={onDone} />
      </div>
    );
  }

  return (
    <form className="op-form" onSubmit={submit}>
      <h3 className="op-h3">Bot-Key erzeugen</h3>
      <label className="op-label">
        Bezeichnung (z. B. „Kanzlei-PC 2")
        <input
          className="op-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          autoFocus
          required
        />
      </label>
      {error && <p className="op-error">{error}</p>}
      <div className="op-form-actions">
        <Button
          variant="primary"
          type="submit"
          loading={busy}
          disabled={!label.trim()}
        >
          Erzeugen
        </Button>
        <button type="button" className="op-btn-ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

export function FirmDetailPage({
  firmId,
  onBack,
  onExpired,
}: {
  firmId: string;
  onBack: () => void;
  onExpired: () => void;
}) {
  const [firm, setFirm] = useState<FirmDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<"none" | "user" | "botkey">("none");
  const [showMandanten, setShowMandanten] = useState(false);

  const load = useCallback(() => {
    setFirm(null);
    setError(null);
    getFirm(firmId)
      .then(setFirm)
      .catch((e: unknown) => {
        if (e instanceof OperatorApiError && e.status === 401) onExpired();
        else setError(e instanceof Error ? e.message : "Fehler beim Laden");
      });
  }, [firmId, onExpired]);

  useEffect(() => {
    load();
  }, [load]);

  function done() {
    setAction("none");
    load();
  }

  return (
    <section>
      <button type="button" className="op-back" onClick={onBack}>
        ‹ Alle Kanzleien
      </button>

      {error ? (
        <p className="op-error">{error}</p>
      ) : !firm ? (
        <Loader label="Kanzlei wird geladen…" />
      ) : (
        <>
          <h1 className="op-h1">{firm.name}</h1>
          <div className="op-slug">
            {firm.slug} · Plan {firm.plan}
            {!firm.active && " · inaktiv"}
          </div>
          {(firm.strasse || firm.ort || firm.telefon || firm.kontakt_email) && (
            <div className="op-contact">
              {[firm.strasse, [firm.plz, firm.ort].filter(Boolean).join(" ")]
                .filter(Boolean)
                .join(", ")}
              {firm.telefon && ` · ☎ ${firm.telefon}`}
              {firm.kontakt_email && ` · ✉ ${firm.kontakt_email}`}
            </div>
          )}

          <div className="op-cards">
            <Stat
              label="Mandanten"
              value={firm.mandanten_count}
              onClick={() => setShowMandanten((v) => !v)}
            />
            <Stat label="Aktive Flows" value={firm.active_workflow_count} />
            <Stat label="Bot-Host installiert" value={firm.installation_count} />
            <Stat label="Geräte-Clients" value={firm.device_client_count} />
            <Stat label="Web-Einladungen" value={firm.web_invite_count} />
            <Stat label="Sachbearbeiter" value={firm.user_count} />
          </div>

          {showMandanten && (
            <MandantenList firmId={firmId} onExpired={onExpired} />
          )}

          <h2 className="op-h2">Verwalten</h2>
          {action === "none" ? (
            <div className="op-form-actions">
              <button
                type="button"
                className="op-btn"
                onClick={() => setAction("user")}
              >
                + Sachbearbeiter anlegen
              </button>
              <button
                type="button"
                className="op-btn"
                onClick={() => setAction("botkey")}
              >
                + Bot-Key erzeugen
              </button>
            </div>
          ) : action === "user" ? (
            <AddUserForm
              firmId={firmId}
              onDone={done}
              onCancel={() => setAction("none")}
            />
          ) : (
            <AddBotKeyForm
              firmId={firmId}
              onDone={done}
              onCancel={() => setAction("none")}
            />
          )}

          <WorkflowsSection firmId={firmId} onExpired={onExpired} />

          <h2 className="op-h2">Sachbearbeiter</h2>
          {firm.users.length === 0 ? (
            <p className="op-empty">Noch keine Sachbearbeiter angelegt.</p>
          ) : (
            <table className="op-table">
              <tbody>
                {firm.users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <strong>{u.full_name}</strong>
                    </td>
                    <td>{u.email}</td>
                    <td>{u.active ? "aktiv" : "inaktiv"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}
